import { useEffect, useMemo, useState } from "react";
import Seo from "@/components/base/Seo";
import StatusBadge from "@/components/base/StatusBadge";
import DataTable, { type DataColumn } from "@/components/base/DataTable";
import AdminPageHeader from "../components/AdminPageHeader";
import AdminToolbar from "../components/AdminToolbar";
import AdminFilterTabs from "../components/AdminFilterTabs";
import { useAuth } from "@/context/AuthContext";
import { getAdminLeads, updateAdminLeadStatus } from "@/services/adminApi";
import type { ContactLead } from "@/types";

const statusTabs = [
  { value: "all", label: "All" },
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "qualified", label: "Qualified" },
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" },
];

const statusOptions: ContactLead["status"][] = ["new", "contacted", "qualified", "won", "lost"];

export default function AdminLeads() {
  const { token } = useAuth();
  const [rows, setRows] = useState<ContactLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    getAdminLeads(token)
      .then((l) => active && setRows(l))
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token]);

  const flash = (msg: string) => {
    setNotice(msg);
    setTimeout(() => setNotice(""), 2200);
  };

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return rows.filter((r) => {
      const matchesStatus = status === "all" || r.status === status;
      const matchesQuery =
        !q ||
        r.name.toLowerCase().includes(q) ||
        r.email.toLowerCase().includes(q) ||
        r.tag.toLowerCase().includes(q) ||
        r.source.toLowerCase().includes(q);
      return matchesStatus && matchesQuery;
    });
  }, [rows, query, status]);

  const updateStatus = async (id: string, next: ContactLead["status"]) => {
    const previous = rows.find((r) => r.id === id)?.status;
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status: next } : r)));
    const ok = await updateAdminLeadStatus(id, next, token);
    if (!ok) {
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status: previous ?? r.status } : r)));
      flash("We couldn't update that lead. Please try again.");
      return;
    }
    flash("Lead status updated.");
  };

  const columns: DataColumn<ContactLead>[] = [
    {
      key: "name",
      header: "Lead",
      render: (r) => (
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-800">
            {r.name.slice(0, 2).toUpperCase()}
          </span>
          <div className="min-w-0">
            <p className="truncate font-semibold text-foreground-950">{r.name}</p>
            <p className="truncate text-xs text-foreground-500">{r.email}</p>
          </div>
        </div>
      ),
    },
    { key: "phone", header: "Phone", render: (r) => <span className="text-foreground-700">{r.phone}</span>, nowrap: true },
    { key: "company", header: "Enquiry", render: (r) => <span className="text-foreground-700">{r.company ?? "—"}</span> },
    {
      key: "source",
      header: "Source",
      nowrap: true,
      render: (r) => (
        <span className="inline-flex items-center gap-1 rounded-full bg-background-100 px-2.5 py-1 text-[11px] font-semibold text-foreground-700">
          <i className="ri-links-line"></i> {r.source}
        </span>
      ),
    },
    {
      key: "tag",
      header: "Tag",
      nowrap: true,
      render: (r) => (
        <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] font-semibold text-secondary-900">
          {r.tag}
        </span>
      ),
    },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} />, nowrap: true },
    { key: "createdAt", header: "Received", render: (r) => <span className="text-foreground-500">{r.createdAt}</span>, nowrap: true },
    {
      key: "actions",
      header: "Update",
      align: "right",
      nowrap: true,
      render: (r) => (
        <select
          value={r.status}
          onChange={(e) => updateStatus(r.id, e.target.value as ContactLead["status"])}
          className="rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-xs font-semibold capitalize text-foreground-700 focus:border-primary-400 focus:outline-none cursor-pointer"
        >
          {statusOptions.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      ),
    },
  ];

  return (
    <>
      <Seo title="Leads" path="/admin/leads" />

      <AdminPageHeader
        icon="ri-contacts-line"
        title="Leads"
        subtitle="Enquiries captured from the website, ready to be worked through."
        actions={
          notice ? (
            <span className="inline-flex items-center gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-2 text-sm text-accent-900">
              <i className="ri-checkbox-circle-line text-accent-600"></i> {notice}
            </span>
          ) : undefined
        }
      />

      <div className="mt-6">
        <AdminToolbar query={query} onQuery={setQuery} placeholder="Search leads…">
          <AdminFilterTabs options={statusTabs} value={status} onChange={setStatus} />
        </AdminToolbar>
      </div>

      <p className="mt-4 text-sm text-foreground-600">
        <span className="font-semibold text-foreground-950">{filtered.length}</span> of {rows.length} leads
      </p>

      <div className="mt-4">
        {loading ? (
          <div className="h-96 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
        ) : error ? (
          <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-6 text-center text-sm text-secondary-900">
            Couldn't load leads. Please refresh.
          </div>
        ) : (
          <DataTable columns={columns} rows={filtered} rowKey={(r) => r.id} minWidth="1080px" />
        )}
      </div>
    </>
  );
}